import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Maps the Figma `<Badge>`, `Status` and `DotStatus` component sets.
 *
 * Every tone carries a text label as well as colour. Colour alone must never
 * convey state — spec §INTERFACES UTILISATEUR requires accessibility, and a
 * colour-only status is invisible to ~8% of male users.
 */
const badge = cva('inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap', {
  variants: {
    tone: {
      neutral: 'bg-slate-100 text-slate-700',
      info: 'bg-brand-50 text-brand-700',
      success: 'bg-green-50 text-green-700',
      warning: 'bg-amber-50 text-amber-800',
      danger: 'bg-rose-50 text-rose-700',
    },
    size: {
      sm: 'px-2 py-0.5 text-2xs',
      md: 'px-2.5 py-1 text-xs',
    },
  },
  defaultVariants: { tone: 'neutral', size: 'md' },
})

const dotTone: Record<NonNullable<VariantProps<typeof badge>['tone']>, string> = {
  neutral: 'bg-slate-400',
  info: 'bg-brand-600',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
}

export type BadgeProps = ComponentProps<'span'> &
  VariantProps<typeof badge> & {
    /** Show a leading status dot. */
    dot?: boolean
    children: ReactNode
  }

export function Badge({ className, tone, size, dot = false, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badge({ tone, size }), className)} {...props}>
      {dot ? (
        <span
          className={cn('size-1.5 shrink-0 rounded-full', dotTone[tone ?? 'neutral'])}
          aria-hidden="true"
        />
      ) : null}
      {children}
    </span>
  )
}

/** Domain status -> tone. Single source so every surface renders alike. */
export const MISSION_STATUS_TONE = {
  DRAFT: 'neutral',
  REQUESTED: 'info',
  DISPATCHING: 'info',
  ACCEPTED: 'info',
  EN_ROUTE_PICKUP: 'info',
  AT_PICKUP: 'info',
  IN_PROGRESS: 'info',
  AT_DROPOFF: 'info',
  DELIVERED: 'success',
  COMPLETED: 'success',
  DISPUTED: 'warning',
  RESOLVED: 'success',
  EXPIRED: 'warning',
  CANCELLED_BY_CLIENT: 'danger',
  CANCELLED_BY_PROVIDER: 'danger',
  CANCELLED_BY_ADMIN: 'danger',
} as const satisfies Record<string, NonNullable<VariantProps<typeof badge>['tone']>>

export const DOCUMENT_STATUS_TONE = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'danger',
} as const satisfies Record<string, NonNullable<VariantProps<typeof badge>['tone']>>

/**
 * Account status, with its label.
 *
 * <p>An earlier version rendered anything that was not ACTIVE as a red
 * "Suspendu". That is wrong and consequential: an operator looking at someone
 * halfway through signup was told the account had been suspended, which is a
 * different situation calling for a different action. The four states are
 * distinct, so they are shown as four states.
 */
export const ACCOUNT_STATUS = {
  ACTIVE: { label: 'Actif', tone: 'success' },
  PENDING_VERIFICATION: { label: 'À vérifier', tone: 'warning' },
  SUSPENDED: { label: 'Suspendu', tone: 'danger' },
  DELETED: { label: 'Supprimé', tone: 'neutral' },
} as const satisfies Record<
  string,
  { label: string; tone: NonNullable<VariantProps<typeof badge>['tone']> }
>

export type AccountStatus = keyof typeof ACCOUNT_STATUS

/** Unknown values are shown verbatim rather than silently mislabelled. */
export function accountStatus(value: string): { label: string; tone: BadgeProps['tone'] } {
  return ACCOUNT_STATUS[value as AccountStatus] ?? { label: value, tone: 'neutral' }
}
